class FormValidator {
  constructor() {
    this.fieldListeners = new WeakMap();
    this.errorSequence = 0;
  }

  validate(formData) {
    const data = this.parse(formData);

    if (!data || !data.form_id) {
      return false;
    }

    const form = document.getElementById(data.form_id);

    if (!form) {
      return false;
    }

    const names = data.names;

    if (!names || typeof names !== 'object') {
      return false;
    }

    if (Array.isArray(names)) {
      const message = this.getMessage(names);

      if (!message) {
        return false;
      }

      this.showFormError(form, message);
      return true;
    }

    let handled = false;

    Object.keys(names).forEach((fieldName) => {
      const message = this.getMessage(names[fieldName]);

      if (!message) {
        return;
      }

      const field = this.getField(form, fieldName);

      if (!field) {
        this.showFormError(form, message);
        handled = true;
        return;
      }

      this.showFieldError(form, field, fieldName, message);
      handled = true;
    });

    return handled;
  }

  parse(formData) {
    if (!formData) {
      return null;
    }

    if (typeof formData === 'string') {
      try {
        return JSON.parse(formData);
      } catch (error) {
        return null;
      }
    }

    return typeof formData === 'object' ? formData : null;
  }

  getMessage(value) {
    if (typeof value === 'string') {
      return value;
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        const message = this.getMessage(item);

        if (message) {
          return message;
        }
      }
    }

    return '';
  }

  getField(form, fieldName) {
    return Array.from(form.elements).find((field) => {
      if (!(field instanceof HTMLElement)) {
        return false;
      }

      return field.name === fieldName || field.name === `${fieldName}[]`;
    });
  }

  getFieldWrapper(field) {
    return (
      field.closest('[data-validation-field]') ||
      field.closest(
        '.meta-input, .meta-select, .meta-checkbox, .meta-upload-file, .btn-upload-input, .meta-checklist'
      ) ||
      field.parentElement
    );
  }

  showFieldError(form, field, fieldName, message) {
    const wrapper = this.getFieldWrapper(field);

    if (!wrapper) {
      this.showFormError(form, message);
      return;
    }

    this.clearFieldError(field, wrapper);

    const error = document.createElement('span');
    const errorId = `validation-error-${++this.errorSequence}`;

    error.id = errorId;
    error.className = 'form-field-error error-text';
    error.dataset.validationErrorFor = fieldName;
    error.setAttribute('role', 'alert');
    error.textContent = message;

    wrapper.classList.add('is-invalid', 'error');
    field.classList.add('is-invalid', 'error');
    field.setAttribute('aria-invalid', 'true');
    this.addDescription(field, errorId);
    wrapper.appendChild(error);
    this.addFieldListeners(field, wrapper);
  }

  showFormError(form, message) {
    this.clearFormError(form);

    const error = document.createElement('div');

    error.className = 'form-error-text';
    error.dataset.validationFormError = 'true';
    error.setAttribute('role', 'alert');
    error.textContent = message;
    form.appendChild(error);
  }

  clearFormError(form) {
    form.querySelectorAll('[data-validation-form-error]').forEach((error) => {
      error.remove();
    });
  }

  clearFieldError(field, wrapper) {
    wrapper.querySelectorAll('[data-validation-error-for]').forEach((error) => {
      error.remove();
    });

    wrapper.classList.remove('is-invalid', 'error');
    field.classList.remove('is-invalid', 'error');
    field.removeAttribute('aria-invalid');
    this.restoreDescription(field);
  }

  addDescription(field, errorId) {
    if (!field.hasAttribute('data-validation-original-describedby')) {
      field.dataset.validationOriginalDescribedby =
        field.getAttribute('aria-describedby') || '';
    }

    const descriptions = field.dataset.validationOriginalDescribedby
      .split(' ')
      .filter(Boolean);

    descriptions.push(errorId);
    field.setAttribute('aria-describedby', descriptions.join(' '));
  }

  restoreDescription(field) {
    if (!field.hasAttribute('data-validation-original-describedby')) {
      return;
    }

    const originalDescription = field.dataset.validationOriginalDescribedby;

    if (originalDescription) {
      field.setAttribute('aria-describedby', originalDescription);
    } else {
      field.removeAttribute('aria-describedby');
    }

    delete field.dataset.validationOriginalDescribedby;
  }

  addFieldListeners(field, wrapper) {
    if (this.fieldListeners.has(field)) {
      return;
    }

    const clear = () => this.clearFieldError(field, wrapper);

    field.addEventListener('input', clear);
    field.addEventListener('change', clear);
    this.fieldListeners.set(field, clear);
  }
}

const formValidator = new FormValidator();

window.formValidation = function (formData) {
  return formValidator.validate(formData);
};
